import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowRight, Bot, CircleAlert, Link2, LoaderCircle, Menu, MessageSquare, Plus, Send, ShieldCheck, SlidersHorizontal, X, Zap } from "lucide-react";
import { apiFetch } from "@/lib/api";
import InlineWork from "@/components/earnos/InlineWork";
import ChatConnections from "@/components/earnos/ChatConnections";

const modes = [
  { value: "auto", label: "Smart mode", detail: "Talk or create a mission when work is requested" },
  { value: "chat", label: "Ask EARNOS", detail: "Answers and research guidance; no external actions" },
  { value: "execute", label: "Complete work", detail: "Create a verified mission; approval required for sensitive actions" },
];

const starterPrompts = [
  {text: "Build a legitimate service offer and prepare the first steps to launch it.", mode: "execute"},
  {text: "Create and publish a customer-ready offer website, with my approval.", mode: "execute"},
  {text: "Find realistic ways to win my first paying customer and verify the next step.", mode: "execute"},
  {text: "Explain three promising income opportunities with realistic costs and risks.", mode: "chat"},
];

export default function EarnosChat() {
  const nav = useNavigate();
  const location = useLocation();
  const requestedMission = new URLSearchParams(location.search).get("mission");
  useEffect(() => {
    if (requestedMission) { setSelectedWork(requestedMission); setWorkOpen(true); }
  }, [requestedMission]);
  const [threads, setThreads] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [workOpen, setWorkOpen] = useState(false);
  const [selectedWork, setSelectedWork] = useState(null);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("auto");
  const [review, setReview] = useState(false);
  const [liveSearch, setLiveSearch] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [appsOpen, setAppsOpen] = useState(false);
  const [connectionTarget, setConnectionTarget] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const viewRef = useRef(null);
  const requestLock = useRef(false);

  const refreshJobs = useCallback(async () => {
    try {
      const rows = await apiFetch("/mission/missions");
      setJobs(Array.isArray(rows) ? rows : []);
    } catch { /* Do not fabricate running jobs on an unavailable API. */ }
  }, []);
  useEffect(() => {
    refreshJobs();
    const interval = setInterval(refreshJobs, 10000);
    return () => clearInterval(interval);
  }, [refreshJobs]);

  const loadThreads = useCallback(async () => {
    const data = await apiFetch("/chats");
    setThreads(Array.isArray(data) ? data : []);
    return Array.isArray(data) ? data : [];
  }, []);

  useEffect(() => {
    let cancelled = false;
    loadThreads().then(rows => {
      if (!cancelled && rows.length) setActive(rows[0].chat_id);
    }).catch(e => !cancelled && setError(e.message || "Cannot load chats"))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [loadThreads]);

  useEffect(() => {
    if (!active) { setMessages([]); return; }
    let current = true;
    apiFetch("/chats/" + encodeURIComponent(active) + "/messages")
      .then(data => {
        if (!current) return;
        const rows = data.messages || [];
        setMessages(rows);
        // Resume a saved work session where the customer left off. A status
        // panel is backed by the actual mission endpoint, never a simulation.
        const latest = [...rows].reverse().find(m => m.mission_id);
        if (requestedMission || latest?.mission_id) {
          setSelectedWork(requestedMission || latest.mission_id);
          setWorkOpen(true);
        } else {
          setSelectedWork(null);
          setWorkOpen(false);
        }
      })
      .catch(e => { if (current) setError(e.message || "Cannot open this chat"); });
    return () => { current = false; };
  }, [active, requestedMission]);

  useEffect(() => {
    if (viewRef.current) viewRef.current.scrollTop = viewRef.current.scrollHeight;
  }, [messages, busy, active]);

  async function newChat() {
    if (busy) return null;
    setError("");
    try {
      const data = await apiFetch("/chats", { method: "POST" });
      await loadThreads();
      setActive(data.chat_id);
      nav("/chat", { replace: true });
      setMessages([]);
      setMenuOpen(false);
      return data.chat_id;
    } catch (e) { setError(e.message); return null; }
  }

  async function send(event) {
    event?.preventDefault();
    const text = input.trim();
    if (!text || requestLock.current) return;
    requestLock.current = true;
    setBusy(true);
    setError("");
    let threadId = active;
    try {
      if (!threadId) {
        const created = await apiFetch("/chats", { method: "POST" });
        threadId = created.chat_id;
        setActive(threadId);
      }
      setInput("");
      const localMessage = { message_id: "local-" + Date.now(), role: "user", content: text };
      setMessages(rows => [...rows, localMessage]);
      const result = await apiFetch("/chats/" + encodeURIComponent(threadId) + "/messages", {
        method: "POST", body: { message: text, mode, review_and_revise: review && mode !== "execute",
          use_live_search: liveSearch && mode === "chat" },
      });
      setMessages(rows => [...rows, result.message]);
      if (result.mission_id) {
        setSelectedWork(result.mission_id);
        setWorkOpen(true);
        await refreshJobs();
      }
      await loadThreads();
    } catch (e) {
      setError(e.message || "EARNOS could not finish this response. Your earlier work remains saved.");
      // Re-load server-owned history so a failed response is never displayed
      // as a successful, persisted model completion.
      if (threadId) {
        try {
          const data = await apiFetch("/chats/" + encodeURIComponent(threadId) + "/messages");
          setMessages(data.messages || []);
        } catch { /* Keep a local draft when offline. */ }
      } else setInput(text);
    } finally { setBusy(false); requestLock.current = false; }
  }

  const runningCount = jobs.filter(j =>
    !["completed", "completed_with_issues", "partially_completed", "failed", "cancelled"].includes(j.state)).length;
  function chooseThread(id) {
    if (busy) return;
    setActive(id); setError(""); setMenuOpen(false);
  }

  return (
    <div className="flex h-dvh w-full overflow-hidden bg-surface-2" data-testid="earnos-full-chat">
      <aside className={(menuOpen ? "flex" : "hidden") + " lg:flex flex-col w-full lg:w-64 shrink-0 border-r border-line bg-surface-3"}>
        <div className="p-4 flex items-center justify-between">
          <span className="font-extrabold tracking-wide text-ink">EARNOS <span className="ml-1 text-xs font-medium text-ink-2">Chats</span></span>
          <button type="button" className="lg:hidden text-ink-2" onClick={() => setMenuOpen(false)} aria-label="Close chat history"><X size={19} /></button>
        </div>
        <button type="button" onClick={newChat} disabled={busy} className="mx-3 mb-3 rounded-xl bg-brand text-white p-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
          <Plus size={18} /> New chat
        </button>
        <div className="overflow-y-auto flex-1 px-3 pb-5 space-y-1">
          {threads.map(t => <button key={t.chat_id} type="button" onClick={() => chooseThread(t.chat_id)}
            className={"w-full flex gap-2 items-center text-left rounded-xl px-3 py-3 text-sm " + (active === t.chat_id ? "bg-brand/15 text-ink border border-brand/30" : "text-ink-2 hover:bg-surface-2")}>
            <MessageSquare size={16} className="shrink-0" />
            <span className="truncate">{t.title || "New conversation"}</span>
          </button>)}
          {!threads.length && !loading ? <p className="text-sm text-ink-2 p-3">Your conversations appear here.</p> : null}
        </div>
        <nav className="p-3 border-t border-line grid grid-cols-2 gap-1" aria-label="EARNOS tools">
          {[["/home","Home"],["/mission","All work"],["/money","Money"],["/connections","Connected apps"],["/engine","Engines"],["/opportunities","Explore"],["/settings","Settings"],["/you","Your account"]].map(([path,label]) =>
            <button type="button" key={path} onClick={() => nav(path)} className="rounded-lg px-3 py-2 text-left text-xs font-medium text-ink-2 hover:bg-surface-2 hover:text-ink">{label}</button>)}
        </nav>
      </aside>
      <div className={(menuOpen ? "hidden" : "flex") + " lg:flex flex-col flex-1 min-w-0"}>
        <header className="flex items-center gap-3 justify-between px-4 sm:px-6 py-4 border-b border-line">
          <div className="flex items-center gap-3 min-w-0">
            <button type="button" aria-label="Open chat history" className="lg:hidden text-ink-2" onClick={() => setMenuOpen(true)}><Menu size={21} /></button>
            <div className="hidden sm:flex w-10 h-10 rounded-xl bg-gradient-to-br from-brand to-brand-violet items-center justify-center"><Bot size={21} className="text-white" /></div>
            <div><h1 className="text-lg font-extrabold text-ink">EARNOS AI</h1>
              <p className="hidden sm:block text-xs text-ink-2">Your AI operator · Live work and approvals</p></div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" onClick={() => {setWorkOpen(x=>!x); refreshJobs();}}
              aria-expanded={workOpen} aria-label="Show current EARNOS work"
              className={"inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold rounded-xl border " +
                (workOpen ? "border-brand text-brand-light bg-brand/10" : "border-line text-ink hover:border-brand")}>
              <Zap size={15}/> Work{runningCount ? " · " + runningCount : ""}
            </button>
            <button type="button" aria-label="Connect apps" onClick={() => setAppsOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-semibold border border-line rounded-xl text-ink hover:border-brand"><Link2 size={15} /><span className="hidden sm:inline">Connect</span></button>
          </div>
        </header>
        {workOpen ? <section aria-label="Your active and saved work"
          data-testid="earnos-chat-work-panel"
          className="border-b border-brand/30 bg-surface-3 p-3 space-y-2 max-h-[69dvh] overflow-auto">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-bold text-ink">Your live Work session</h2>
            <button type="button" aria-label="Close work panel" onClick={()=>setWorkOpen(false)}
              className="text-ink-2"><X size={17}/></button>
          </div>
          {jobs.length ? <div className="flex gap-2 overflow-x-auto pb-1">
            {jobs.slice(0,12).map(job =>
              <button type="button" key={job.mission_id}
                onClick={()=>{setSelectedWork(job.mission_id); nav("/chat?mission=" + encodeURIComponent(job.mission_id), {replace:true});}}
                aria-pressed={selectedWork === job.mission_id}
                className={"shrink-0 max-w-56 rounded-xl border p-3 text-left " +
                  (selectedWork === job.mission_id ? "border-brand bg-brand/10" : "border-line bg-surface-2")}>
                <span className="block truncate text-xs font-bold text-ink">{job.goal}</span>
                <span className="text-[11px] text-ink-2 capitalize">{(job.state || "queued").replaceAll("_"," ")}</span>
              </button>)}
          </div> :
            <p className="text-xs text-ink-2">Describe the result you want in this chat. Each real mission shows its steps, approvals and evidence here.</p>}
          {selectedWork ? <InlineWork missionId={selectedWork}/> : null}
        </section> : null}
        <div ref={viewRef} className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-6 space-y-5" role="log" aria-live="polite">
          {!messages.length ? <div className="max-w-xl mx-auto py-10 space-y-5">
            <div className="w-14 h-14 rounded-2xl bg-brand/20 text-brand-light flex items-center justify-center"><Bot size={31} /></div>
            <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-ink">What do you want to accomplish?</h2>
            <p className="text-ink-2">Tell EARNOS the result you want. Watch real progress in this chat, connect your own accounts, approve specific actions, and open results without downloading them.</p>
            <div className="grid sm:grid-cols-2 gap-2">{starterPrompts.map(item =>
              <button type="button" key={item.text} onClick={() => {setInput(item.text); setMode(item.mode);}}
                className="text-left rounded-2xl p-4 border border-line bg-surface-3 hover:border-brand text-sm text-ink">
                <Zap size={17} className="text-brand-light mb-2"/>
                {item.text}
              </button>)}</div>
          </div> : messages.map(m =>
            <div key={m.message_id} className={"flex max-w-3xl mx-auto " + (m.role === "user" ? "justify-end" : "justify-start")}>
              <div className={"max-w-[92%] sm:max-w-[84%] rounded-2xl p-4 whitespace-pre-wrap break-words text-sm sm:text-base leading-7 " + (m.role === "user" ? "bg-brand text-white" : "bg-surface-3 text-ink border border-line")}>
                {m.role !== "user" ? <div className="mb-2 text-xs font-bold tracking-wide text-brand-light">EARNOS</div> : null}
                {m.content}
                {m.connection_intents?.length ? <div className="mt-3 space-y-2" aria-label="Requested app connections">{m.connection_intents.map(service => <button key={service} type="button" onClick={() => {setConnectionTarget({service, goal: m.connection_goal || ""}); setAppsOpen(true);}} className="w-full rounded-xl border border-brand/40 bg-brand/10 p-3 text-left text-sm font-bold text-ink"><Link2 size={16} className="inline mr-2"/> Connect {service}<span className="block mt-1 text-xs font-normal text-ink-2">Check real sign-in and supported actions</span></button>)}</div> : null}
                {m.sources?.length ? <div className="mt-4 space-y-2 text-xs">
                  <p className="font-semibold text-ink-2">Search results — summaries not independently fact-checked</p>
                  {m.sources.map((source, idx) => <a key={idx}
                    className="block truncate text-brand-light underline underline-offset-2"
                    href={source.url} target="_blank" rel="noopener noreferrer">{idx + 1}. {source.title || source.url}</a>)}
                </div> : null}
                {m.mission_id ? <button type="button"
                  onClick={() => {setSelectedWork(m.mission_id); setWorkOpen(true); nav("/chat?mission=" + encodeURIComponent(m.mission_id), {replace:true});}}
                  className="mt-3 flex items-center gap-2 rounded-xl border border-brand/40 bg-brand/10 px-3 py-2 font-bold text-sm text-brand-light">
                  <Zap size={16}/> Watch live work <ArrowRight size={16}/>
                </button> : m.route && m.route_label ? <button type="button" onClick={() => nav(m.route)}
                  className="mt-3 flex items-center gap-2 rounded-xl border border-brand/40 bg-brand/10 px-3 py-2 font-bold text-sm text-brand-light">
                  {m.route_label} <ArrowRight size={16} />
                </button> : null}
              </div>
            </div>)}
          {busy ? <div className="flex gap-2 items-center text-sm text-ink-2"><LoaderCircle size={16} className="animate-spin" /> EARNOS is working on your response…</div> : null}
        </div>
        {error ? <p role="alert" className="mx-4 mb-2 p-2 rounded-lg bg-danger/10 text-danger text-xs flex items-start gap-2"><CircleAlert size={15} /> {error}</p> : null}
        <form onSubmit={send} className="w-full max-w-3xl mx-auto shrink-0 p-3 sm:p-4 space-y-3 bg-surface-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center gap-2 flex-wrap" role="group" aria-label="How EARNOS should help">
            <div className="inline-flex rounded-xl border border-line bg-surface-3 p-1 gap-1">
              {modes.map(m => (
                <button type="button" key={m.value} disabled={busy}
                  aria-pressed={mode === m.value} onClick={() => setMode(m.value)}
                  className={"rounded-lg px-3 py-2 text-xs sm:text-sm font-semibold disabled:opacity-50 " +
                    (mode === m.value ? "bg-brand text-white" : "text-ink-2 hover:text-ink")}>
                  {m.value === "execute" ? "Do the work" : m.value === "chat" ? "Ask" : "Auto"}
                </button>
              ))}
            </div>
            <button type="button" disabled={busy}
              aria-expanded={advancedOpen} onClick={() => setAdvancedOpen(x=>!x)}
              className="ml-auto flex items-center gap-1.5 text-xs text-ink-2 px-2 py-2">
              <SlidersHorizontal size={15}/> Options
            </button>
          </div>
          {advancedOpen ? <div className="flex flex-wrap items-center gap-3 text-xs text-ink-2 rounded-xl border border-line p-3">
            <p className="w-full">{modes.find(m => m.value === mode)?.detail}</p>
            <label className="flex gap-2 items-center cursor-pointer">
              <input type="checkbox" checked={liveSearch} disabled={busy || mode !== "chat"}
                onChange={e=>setLiveSearch(e.target.checked)} className="accent-brand"/>
              Live web search for this question
            </label>
            <label className="flex gap-2 items-center cursor-pointer">
              <input type="checkbox" checked={review} disabled={busy || mode === "execute"}
                onChange={e=>setReview(e.target.checked)} className="accent-brand"/>
              Optional second-model review (extra credits)
            </label>
          </div> : null}
          <div className="flex gap-2 items-end rounded-2xl border border-line bg-surface-3 p-2 focus-within:border-brand">
            <button type="button" aria-label="Connect an app or website"
              onClick={() => setAppsOpen(true)} disabled={busy}
              className="w-11 h-11 rounded-xl border border-line flex items-center justify-center shrink-0 text-ink hover:border-brand disabled:opacity-50">
              <Plus size={20}/>
            </button>
            <textarea data-testid="earnos-chat-message" aria-label="Message EARNOS" rows={2} maxLength={2000} value={input} disabled={busy}
              onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); if (input.trim()) send(); } }}
              placeholder={mode === "execute" ? "What should EARNOS do for you? Connect, complete, verify…" : "Ask anything, or describe the job you want completed…"}
              className="min-h-14 min-w-0 flex-1 bg-transparent p-2 text-ink resize-none outline-none placeholder:text-ink-2" />
            <button aria-label="Send message" disabled={busy || !input.trim()} type="submit"
              className="rounded-xl bg-brand text-white w-11 h-11 flex items-center justify-center shrink-0 disabled:opacity-40"><Send size={20} /></button>
          </div>
          <p className="text-center text-[11px] text-ink-2 flex justify-center items-center gap-1">
            <ShieldCheck size={13} /> Customer-controlled sign-ins · Approve outside actions · Verify results
          </p>
        </form>
      </div>
      {appsOpen ? <ChatConnections initialService={connectionTarget?.service || ""} initialGoal={connectionTarget?.goal || ""} onClose={() => {setAppsOpen(false); setConnectionTarget(null);}} /> : null}
    </div>
  );
}
